import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { toast } from 'react-toastify';

import { Images } from 'util/images';
import { useActions } from 'hooks/useActions';
import * as actions from './actions';
import { Spinner } from 'components/Spinner/spinner';
import Pagination from 'user/components/Pagination';
import QuestionFeedbackModal from 'components/Modals/questionFeedbackModal';
import { getConfig } from 'functions/getConfig';
import { Wrapper, Container, Button, LinkButton } from '../../components';
import { Header, HeadingContainer } from '../ChallengeListPage/styles';
import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableHeading,
  TableData,
  SortArrowImage,
} from '../../components/cmsRankList/style';
import { FlexContainer, SearchContainer, SearchInput, ActionButtons } from './styles';

export default function QuestionFeedbackPage() {
  const [questionFeedbacks, setQuestionFeedbacks] = useState([]);
  const [paginatedItems, setPaginatedItems] = useState([]);
  const [testName, setTestName] = useState('');
  const [accountName, setAccountName] = useState('');
  const [question, setQuestion] = useState('');
  const [defect, setDefect] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [isAscending, setIsAscending] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [modalInfo, setModalInfo] = useState({});

  const config = getConfig();
  const itemsPerPage = 10;

  const { loadQuestionFeedbacks, updateQuestionFeedback } = useActions(actions);
  let { loading, feedbacks, tests, questions, accounts, feedback, error } = useSelector(
    (state) => state.questionFeedback
  );

  useEffect(() => {
    loadQuestionFeedbacks();
  }, [loadQuestionFeedbacks]);

  useEffect(() => {
    if (feedbacks.length) {
      setQuestionFeedbacks(feedbacks);
      if (feedbacks.length <= itemsPerPage) setPaginatedItems(feedbacks);
    }
  }, [feedbacks]);

  useEffect(() => {
    if (feedback._id) toast.success('Feedback updated successfully');
  }, [feedback]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  useEffect(() => {
    let feedbacksCopy = JSON.parse(JSON.stringify(feedbacks));
    if (testName)
      feedbacksCopy = feedbacksCopy.filter(
        (feedback) => String(tests[feedback.test].name).toLowerCase().indexOf(testName.toLowerCase()) > -1
      );
    if (accountName)
      feedbacksCopy = feedbacksCopy.filter(
        (feedback) => String(accounts[feedback.account].name).toLowerCase().indexOf(accountName.toLowerCase()) > -1
      );
    if (question)
      feedbacksCopy = feedbacksCopy.filter(
        (feedback) => String(questions[feedback.question].value).toLowerCase().indexOf(question.toLowerCase()) > -1
      );
    if (defect)
      feedbacksCopy = feedbacksCopy.filter(
        (feedback) => String(feedback.defect).toLowerCase().indexOf(defect.toLowerCase()) > -1
      );
    setQuestionFeedbacks(feedbacksCopy);
    if (feedbacksCopy.length <= itemsPerPage) setPaginatedItems(feedbacksCopy);
  }, [tests, testName, accounts, accountName, questions, question, defect, feedbacks]);

  const sortListBy = (field) => {
    let updatedList = sort(questionFeedbacks, field, field !== sortBy ? false : isAscending);
    setQuestionFeedbacks(updatedList);
    if (field !== sortBy) {
      setIsAscending(true);
    } else {
      setIsAscending(!isAscending);
    }
    setSortBy(field);
  };

  const sort = (array, field, isAscending) => {
    let sortableItems = [...array];
    return sortableItems.sort((a, b) => {
      let x = a[field];
      let y = b[field];
      if (field === 'testName') {
        x = tests[a.test].name.toLowerCase();
        y = tests[b.test].name.toLowerCase();
      }
      if (field === 'defect') {
        x = a.defect.toLowerCase();
        y = b.defect.toLowerCase();
      }
      if (x < y) {
        return isAscending ? -1 : 1;
      }
      if (x > y) {
        return isAscending ? 1 : -1;
      }
      return 0;
    });
  };

  const getQuestion = (question) => {
    let index = question.indexOf('<p>');
    return (
      question.slice(0, index + 3) + '<span style="font-weight: 600">Question:</span> ' + question.slice(index + 3)
    );
  };

  const updateModalInfo = (feedback, isRejected) => {
    setShowFeedbackModal(true);
    getQuestion(questions[feedback.question].value);
    setModalInfo({
      title: `${isRejected ? 'Reject' : 'Update'} Feedback`,
      test: tests[feedback.test].name,
      question: getQuestion(questions[feedback.question].value),
      isRejected: isRejected,
      _id: feedback._id,
    });
  };

  const renderActionButtons = (feedback) => {
    return (
      <ActionButtons>
        <LinkButton
          primary
          primaryGreenBorder
          small
          href={`${config.REDIRECTION_URL}/edit-test/${feedback.test}/${feedback.question}`}
        >
          Update
        </LinkButton>
        <Button primaryRed small onClick={() => updateModalInfo(feedback, true)}>
          Reject
        </Button>
        <Button primary primaryGreenBorder small onClick={() => updateModalInfo(feedback, false)}>
          Mark as updated
        </Button>
      </ActionButtons>
    );
  };

  const reportQuestionFeedback = (data) => {
    setShowFeedbackModal(false);
    if (!data.isSubmitted) return;
    let params = {
      _id: data._id,
    };
    if (data.isRejected) params.rejectMessage = data.message;
    else params.updateMessage = data.message;
    updateQuestionFeedback(params);
    setModalInfo({});
  };

  const renderTableHeading = () => {
    return (
      <TableRow isHeading>
        <TableHeading>
          <FlexContainer>
            <SearchContainer>
              <SearchInput
                placeholder="Test"
                value={testName}
                onChange={(e) => setTestName(e.target.value)}
              ></SearchInput>
            </SearchContainer>
            <SortArrowImage
              onClick={() => sortListBy('testName')}
              alt="arrow"
              src={sortBy === 'testName' && isAscending ? Images.upArrow : Images.downArrow}
            />
          </FlexContainer>
        </TableHeading>
        <TableHeading hideOnSmallDevice>
          <SearchContainer>
            <SearchInput
              placeholder="Reported By"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
            ></SearchInput>
          </SearchContainer>
        </TableHeading>
        <TableHeading>
          <SearchContainer>
            <SearchInput
              placeholder="Question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
            ></SearchInput>
          </SearchContainer>
        </TableHeading>
        <TableHeading hideOnSmallDevice>
          <FlexContainer>
            <SearchContainer>
              <SearchInput
                placeholder="Defect"
                value={defect}
                onChange={(e) => setDefect(e.target.value)}
              ></SearchInput>
            </SearchContainer>
            <SortArrowImage
              onClick={() => sortListBy('defect')}
              alt="arrow"
              src={sortBy === 'defect' && isAscending ? Images.upArrow : Images.downArrow}
            />
          </FlexContainer>
        </TableHeading>
        <TableHeading>Feedback</TableHeading>
        <TableHeading hideOnSmallDevice>
          Created At{' '}
          <SortArrowImage
            onClick={() => sortListBy('createdAt')}
            alt="arrow"
            src={sortBy === 'createdAt' && isAscending ? Images.upArrow : Images.downArrow}
          />
        </TableHeading>
        <TableHeading hideOnSmallDevice>
          Updated At{' '}
          <SortArrowImage
            onClick={() => sortListBy('updatedAt')}
            alt="arrow"
            src={sortBy === 'updatedAt' && isAscending ? Images.upArrow : Images.downArrow}
          />
        </TableHeading>
        <TableHeading hideOnSmallDevice>Action & note</TableHeading>
      </TableRow>
    );
  };

  const renderTableBody = () => {
    return paginatedItems.map((item, index) => {
      return (
        <TableRow key={index}>
          <TableData>{tests[item.test].name}</TableData>
          <TableData hideOnSmallDevice>{accounts[item.account].name ?? 'Candidate'}</TableData>
          <TableData alignLeft dangerouslySetInnerHTML={{ __html: questions[item.question].value }}></TableData>
          <TableData hideOnSmallDevice>{item.defect}</TableData>
          <TableData isFeedbackRow>{item.feedback}</TableData>
          <TableData minWidth="110px" hideOnSmallDevice>
            {moment(item.createdAt).format('YYYY-MM-DD')}
          </TableData>
          <TableData minWidth="115px" hideOnSmallDevice>
            {moment(item.updatedAt).format('YYYY-MM-DD')}
          </TableData>
          <TableData hideOnSmallDevice isFeedbackRow>
            {item.updateMessage ? (
              <>
                <b>Updated</b>: {item.updateMessage}
              </>
            ) : item.rejectMessage ? (
              <>
                <b>Rejected</b>: {item.rejectMessage}
              </>
            ) : (
              renderActionButtons(item)
            )}
          </TableData>
        </TableRow>
      );
    });
  };

  return (
    <Wrapper padding="60px 0 0 0">
      {showFeedbackModal && (
        <QuestionFeedbackModal modalInfo={modalInfo} confirm={(data) => reportQuestionFeedback(data)} />
      )}
      <Container column full usePadding="20px 50px 50px 50px">
        <HeadingContainer>
          <Header>Question Feedbacks</Header>
        </HeadingContainer>
        {loading && <Spinner />}
        <Table margin="30px 0 0 0">
          <TableHead>{renderTableHeading()}</TableHead>
          <TableBody>{renderTableBody()}</TableBody>
        </Table>
        {questionFeedbacks.length > itemsPerPage && (
          <Pagination items={questionFeedbacks} itemsPerPage={itemsPerPage} getItems={setPaginatedItems} />
        )}
      </Container>
    </Wrapper>
  );
}
